import { useState, useMemo, useEffect } from 'react'
import { usePlayerStore, useAuthStore } from '../stores'
import PlayerCard from '../components/PlayerCard'
import PlayerDetail from '../components/PlayerDetail'
import PlayerSearch from '../components/PlayerSearch'
import { Filter, ArrowUpDown, X } from 'lucide-react'

type SortField = 'name' | 'age' | 'goals' | 'assists' | 'recent' | 'passes' | 'potential' | 'position'
type SortOrder = 'asc' | 'desc'
type FilterPosition = 'all' | 'GK' | 'DF' | 'MF' | 'FW'
type FilterLeague = 'all' | 'NPFL' | 'NNL' | 'NWFL' | 'NLO' | 'TCC' | 'NextGen' | 'N-Youth League' | 'Others' | 'International'

interface ExplorePageProps {
  onLoginClick: () => void
  selectedPlayerId?: string | null
}

const POSITION_MAP: Record<FilterPosition, string[]> = {
  all: [],
  GK: ['GK'],
  DF: ['CB', 'LB', 'RB', 'RWB', 'LWB'],
  MF: ['CDM', 'CM', 'CAM', 'LM', 'RM'],
  FW: ['LW', 'RW', 'SS', 'CF', 'ST'],
}

const KNOWN_LEAGUES = ['NPFL', 'NNL', 'NWFL', 'NLO', 'TCC', 'NextGen', 'N-Youth League', 'International']

function ExplorePage({ onLoginClick, selectedPlayerId }: ExplorePageProps) {
  const { players, clubs } = usePlayerStore()
  const { user } = useAuthStore()
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(selectedPlayerId || null)
  const isGuest = !user

  useEffect(() => {
    if (selectedPlayerId) {
      setSelectedPlayer(selectedPlayerId)
    }
  }, [selectedPlayerId])

  const [searchResults, setSearchResults] = useState(players)

  const [filterPosition, setFilterPosition] = useState<FilterPosition>('all')
  const [filterLeague, setFilterLeague] = useState<FilterLeague>('all')
  const [showFilterMenu, setShowFilterMenu] = useState(false)
  const [showSortMenu, setShowSortMenu] = useState(false)
  const [compareIds, setCompareIds] = useState<string[]>([])

  const [sortField, setSortField] = useState<SortField>('age')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')

  const verifiedPlayers = useMemo(() => {
    const clubArray = Array.isArray(clubs) ? clubs : []
    const visibleClubIds = new Set(clubArray.filter((club) => club.is_visible !== false).map((club) => club.id))
    const hiddenClubNames = new Set(
      clubArray
        .filter((club) => club.is_visible === false)
        .map((club) => club.name?.trim().toLowerCase())
        .filter(Boolean)
    )
    const eligiblePlayers = players.filter((p) => {
      if (p.is_public === false) return false
      if (p.owner_club_id && !visibleClubIds.has(p.owner_club_id)) return false
      if (p.club && hiddenClubNames.has(p.club.trim().toLowerCase())) return false
      return true
    })
    return eligiblePlayers
  }, [players, clubs])

  const filteredAndSortedPlayers = useMemo(() => {
    let results = [...searchResults]

    // Apply position filter
    if (filterPosition !== 'all') {
      const allowedPositions = POSITION_MAP[filterPosition]
      results = results.filter((p) => {
        const pos = (p.main_position || '').toUpperCase()
        const positions = (p.positions || []).map((x) => x.toUpperCase())
        return allowedPositions.some((allowed) => pos.includes(allowed) || positions.some((pp) => pp.includes(allowed)))
      })
    }

    // Apply league filter
    if (filterLeague !== 'all') {
      results = results.filter((p) => {
        const league = (p.league || '').trim()
        if (filterLeague === 'Others') {
          return league && !KNOWN_LEAGUES.some((kl) => league.toLowerCase().includes(kl.toLowerCase()))
        }
        return league.toLowerCase().includes(filterLeague.toLowerCase())
      })
    }

    // Apply sorting
    results.sort((a, b) => {
      let compareValue = 0

      if (sortField === 'name') {
        compareValue = a.name.localeCompare(b.name)
      } else if (sortField === 'age') {
        compareValue = a.age - b.age
      } else if (sortField === 'goals') {
        compareValue = (b.goals || 0) - (a.goals || 0)
      } else if (sortField === 'assists') {
        compareValue = (b.assists || 0) - (a.assists || 0)
      } else if (sortField === 'passes') {
        compareValue = (b.kp || 0) - (a.kp || 0)
      } else if (sortField === 'potential') {
        compareValue = (b.potential || 0) - (a.potential || 0)
      } else if (sortField === 'position') {
        const positionOrder = ['GK', 'CB', 'LB', 'RB', 'CDM', 'CM', 'CAM', 'RWF', 'LWF', 'SS', 'CF']
        compareValue = positionOrder.indexOf(a.main_position) - positionOrder.indexOf(b.main_position)
      } else if (sortField === 'recent') {
        compareValue = new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()
      }

      return sortOrder === 'asc' ? compareValue : -compareValue
    })

    return results
  }, [searchResults, filterPosition, filterLeague, sortField, sortOrder])

  const POSITIONS: { id: FilterPosition; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'GK', label: 'GK' },
    { id: 'DF', label: 'DF' },
    { id: 'MF', label: 'MF' },
    { id: 'FW', label: 'FW' },
  ]

  const LEAGUES: { id: FilterLeague; label: string }[] = [
    { id: 'all', label: 'All Leagues' },
    { id: 'NPFL', label: 'NPFL' },
    { id: 'NNL', label: 'NNL' },
    { id: 'NWFL', label: 'NWFL' },
    { id: 'NLO', label: 'NLO' },
    { id: 'TCC', label: 'TCC' },
    { id: 'NextGen', label: 'NextGen' },
    { id: 'N-Youth League', label: 'N-Youth League' },
    { id: 'International', label: 'International' },
    { id: 'Others', label: 'Others' },
  ]

  const SORT_OPTIONS: { id: SortField; label: string }[] = [
    { id: 'age', label: 'Age' },
    { id: 'goals', label: 'Goals' },
    { id: 'assists', label: 'Assists' },
    { id: 'passes', label: 'Passes' },
    { id: 'potential', label: 'Potential' },
    { id: 'position', label: 'Position' },
    { id: 'name', label: 'Name' },
    { id: 'recent', label: 'Recent' },
  ]

  const hasActiveFilters = filterPosition !== 'all' || filterLeague !== 'all'
  const selectedComparePlayers = verifiedPlayers.filter((player) => compareIds.includes(player.id))

  const toggleComparePlayer = (playerId: string) => {
    setCompareIds((current) => {
      if (current.includes(playerId)) {
        return current.filter((id) => id !== playerId)
      }
      if (current.length >= 2) {
        return [...current.slice(1), playerId]
      }
      return [...current, playerId]
    })
  }

  const clearFilters = () => {
    setFilterPosition('all')
    setFilterLeague('all')
  }

  return (
    <div className="min-h-screen surface-bg">
      {selectedPlayer ? (
        (() => {
          const player = verifiedPlayers.find((p) => p.id === selectedPlayer)
          return player ? (
            <div className="max-w-7xl mx-auto px-4 py-8">
              <PlayerDetail
                player={player}
                onBack={() => setSelectedPlayer(null)}
                canEdit={false}
              />
            </div>
          ) : (
            <div className="max-w-7xl mx-auto px-4 py-8">
              <div className="surface-panel-low rounded-3xl p-12 text-center border border-white/10 shadow-tactical">
                <p className="text-slate-200 mb-2">This profile is not available.</p>
                <p className="text-slate-300 text-sm">It may be pending approval or the link is invalid.</p>
                <button
                  onClick={() => setSelectedPlayer(null)}
                  className="mt-4 btn-primary"
                >
                  Back to Explore
                </button>
              </div>
            </div>
          )
        })()
      ) : (
        <>
          <div className="bg-[#0a151f] border-b border-white/5 py-12">
            <div className="max-w-7xl mx-auto px-4">
              <h1 className="text-4xl font-display font-bold text-white mb-2">Explore Talent</h1>
              <p className="text-slate-200">Discover and analyze the next generation of Nigerian football stars.</p>
            </div>
          </div>

          <main className="max-w-7xl mx-auto px-4 py-8">
            <div className="space-y-3 mb-8">
              <div className="surface-glass p-4 backdrop-blur-xl border border-white/5 rounded-3xl shadow-tactical">
                <PlayerSearch
                  players={verifiedPlayers}
                  onSearch={setSearchResults}
                  placeholder="Search for players..."
                />
                {isGuest && (
                  <div className="mt-3 rounded-2xl border border-yellow-400/40 bg-yellow-500/10 p-3 text-sm text-yellow-200 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <p className="text-left font-display">
                      Guests can search and filter the database. Sign in to unlock scouting actions and verified reports.
                    </p>
                    <button
                      onClick={onLoginClick}
                      className="btn-tertiary px-4 py-2 text-xs font-bold shrink-0"
                    >
                      Log In / Sign Up
                    </button>
                  </div>
                )}
              </div>

              {/* Toolbar */}
              <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap hide-scrollbar">
                <button
                  onClick={() => { setShowFilterMenu(!showFilterMenu); setShowSortMenu(false) }}
                  className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest border transition-all ${
                    showFilterMenu || hasActiveFilters
                      ? 'bg-[#7cff01]/10 border-[#7cff01]/30 text-[#7cff01]'
                      : 'bg-white/5 border-white/10 text-gray-400 hover:text-white'
                  }`}
                >
                  <Filter size={14} />
                  Filter
                  {hasActiveFilters && (
                    <span className="w-5 h-5 rounded-full bg-[#7cff01] text-black text-[10px] flex items-center justify-center font-bold">
                      {(filterPosition !== 'all' ? 1 : 0) + (filterLeague !== 'all' ? 1 : 0)}
                    </span>
                  )}
                </button>

                <button
                  onClick={() => { setShowSortMenu(!showSortMenu); setShowFilterMenu(false) }}
                  className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest border transition-all ${
                    showSortMenu
                      ? 'bg-[#7cff01]/10 border-[#7cff01]/30 text-[#7cff01]'
                      : 'bg-white/5 border-white/10 text-gray-400 hover:text-white'
                  }`}
                >
                  <ArrowUpDown size={14} />
                  {SORT_OPTIONS.find(o => o.id === sortField)?.label || 'Sort'}
                </button>

                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="shrink-0 px-4 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest border bg-white/5 border-white/10 text-gray-400 hover:text-white transition-all"
                >
                  {sortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
                </button>

                <div className="shrink-0 ml-auto px-4 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest border bg-[#021a1a] border-white/5 text-gray-400">
                  {filteredAndSortedPlayers.length} {filteredAndSortedPlayers.length === 1 ? 'talent' : 'talents'}
                </div>
              </div>

              {/* Filter Panel — one-tap buttons */}
              {showFilterMenu && (
                <div className="mt-2 surface-glass border border-white/10 rounded-3xl p-5 shadow-tactical animate-in slide-in-from-top-2 fade-in duration-200 space-y-5">
                  {/* Position Buttons */}
                  <div>
                    <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3">Position</p>
                    <div className="flex flex-wrap gap-2">
                      {POSITIONS.map((pos) => (
                        <button
                          key={pos.id}
                          onClick={() => setFilterPosition(pos.id)}
                          className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest border transition-all ${
                            filterPosition === pos.id
                              ? 'bg-[#7cff01]/10 border-[#7cff01]/50 text-[#7cff01]'
                              : 'bg-white/5 border-white/10 text-gray-400 hover:text-white hover:border-white/20'
                          }`}
                        >
                          {pos.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* League Buttons */}
                  <div>
                    <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3">League</p>
                    <div className="flex flex-wrap gap-2">
                      {LEAGUES.map((league) => (
                        <button
                          key={league.id}
                          onClick={() => setFilterLeague(league.id)}
                          className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest border transition-all ${
                            filterLeague === league.id
                              ? 'bg-[#7cff01]/10 border-[#7cff01]/50 text-[#7cff01]'
                              : 'bg-white/5 border-white/10 text-gray-400 hover:text-white hover:border-white/20'
                          }`}
                        >
                          {league.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Close */}
                  <div className="flex justify-end">
                    <button
                      onClick={() => setShowFilterMenu(false)}
                      className="btn-primary px-6 py-2"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}

              {/* Sort Panel — one-tap buttons */}
              {showSortMenu && (
                <div className="mt-2 surface-glass border border-white/10 rounded-3xl p-5 shadow-tactical animate-in slide-in-from-top-2 fade-in duration-200">
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3">Sort By</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {SORT_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => {
                          setSortField(option.id)
                          setShowSortMenu(false)
                        }}
                        className={`text-left px-4 py-3 rounded-xl text-sm transition-colors border ${
                          sortField === option.id
                            ? 'bg-[#7cff01]/10 border-[#7cff01]/50 text-[#7cff01] font-bold'
                            : 'bg-white/5 border-transparent text-slate-100 hover:bg-white/10'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Active Filters Display */}
              {hasActiveFilters && (
                <div className="surface-panel-low rounded-3xl p-4 flex flex-wrap gap-2 items-center border border-white/10 shadow-tactical">
                  <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Active:</span>
                  {filterPosition !== 'all' && (
                    <button
                      onClick={() => setFilterPosition('all')}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#7cff01]/10 border border-[#7cff01]/20 text-[#7cff01] text-[10px] font-black uppercase tracking-widest rounded-full hover:bg-[#7cff01]/20 transition"
                    >
                      {filterPosition}
                      <X size={10} />
                    </button>
                  )}
                  {filterLeague !== 'all' && (
                    <button
                      onClick={() => setFilterLeague('all')}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#7cff01]/10 border border-[#7cff01]/20 text-[#7cff01] text-[10px] font-black uppercase tracking-widest rounded-full hover:bg-[#7cff01]/20 transition"
                    >
                      {filterLeague}
                      <X size={10} />
                    </button>
                  )}
                  <button
                    onClick={clearFilters}
                    className="text-[10px] font-black text-gray-500 hover:text-white uppercase tracking-widest ml-1 transition"
                  >
                    Clear All
                  </button>
                </div>
              )}
            </div>

            {selectedComparePlayers.length > 0 && (
              <div className="mb-6 rounded-3xl border border-[#7cff01]/20 bg-[#071610]/80 p-4 shadow-tactical">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.28em] text-[#7cff01]">Compare</p>
                    <h3 className="mt-1 text-xl font-black text-white">Selected players</h3>
                  </div>
                  <button
                    onClick={() => setCompareIds([])}
                    className="text-xs font-black uppercase tracking-[0.22em] text-slate-300 hover:text-white"
                  >
                    Clear
                  </button>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  {selectedComparePlayers.map((player) => (
                    <div key={player.id} className="rounded-2xl border border-white/10 bg-slate-950/60 p-3">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 overflow-hidden rounded-xl border border-white/10 bg-slate-900">
                          <img src={player.photo_url || '/placeholder-user.png'} alt={player.name} className="h-full w-full object-cover" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-base font-black text-white">{player.name}</p>
                          <p className="text-xs text-slate-400">{player.main_position || 'N/A'} • {player.age} yrs</p>
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-300">
                        <div className="rounded-xl bg-white/5 p-2"><span className="text-slate-500">Goals</span><div className="mt-1 text-base font-black text-[#7cff01]">{player.goals ?? 0}</div></div>
                        <div className="rounded-xl bg-white/5 p-2"><span className="text-slate-500">Assists</span><div className="mt-1 text-base font-black text-[#7cff01]">{player.assists ?? 0}</div></div>
                        <div className="rounded-xl bg-white/5 p-2"><span className="text-slate-500">Potential</span><div className="mt-1 text-base font-black text-[#7cff01]">{player.potential ?? 0}</div></div>
                        <div className="rounded-xl bg-white/5 p-2"><span className="text-slate-500">Club</span><div className="mt-1 text-base font-black text-[#7cff01] truncate">{player.club || 'Independent'}</div></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {filteredAndSortedPlayers.length === 0 ? (
              <div className="surface-panel-low rounded-3xl p-12 text-center border border-white/10 shadow-tactical">
                <p className="text-slate-200 mb-2">No talents found matching your criteria</p>
                <p className="text-slate-300 text-sm">Try adjusting your search or filters</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-5 px-1 sm:px-0">
                {filteredAndSortedPlayers.map(player => (
                  <PlayerCard
                    key={player.id}
                    player={player}
                    onViewProfile={(id) => setSelectedPlayer(id)}
                    onCompare={toggleComparePlayer}
                    isCompared={compareIds.includes(player.id)}
                  />
                ))}
              </div>
            )}
          </main>
        </>
      )}
    </div>
  )
}

export default ExplorePage
